import { produce } from 'immer';
import { useEffect, useRef, useState } from 'react';
import { SelectedLegendItemRecordState, SelectedLegendItemState } from '@/components';
import { PieChartDataset } from '@/components/PieChartBase';

// This code is copied from the time series chart with some modifications to get it to work
// for pie chart data which is a different shape. It also needed some special handling for the
// aggregated slice because in this case a single dataset item corresponds to multiple legend items.

export interface UseSelectedLegendItemsProps {
  pieChartData: PieChartDataset;
  /**
   * When specified, the chart will attempt to initialize with specified item focused in the
   * legend on intialization.
   */
  focusedItemId?: string;
}

export interface UseSelectedLegendItemsResponse {
  selectedLegendItems: SelectedLegendItemState;
  setSelectedLegendItems: React.Dispatch<React.SetStateAction<SelectedLegendItemState>>;
}

function updateSelectedLegendItemsWithNewData(
  prevSelectedLegendItems: SelectedLegendItemState,
  pieChartData: PieChartDataset
): SelectedLegendItemState {
  if (prevSelectedLegendItems === 'ALL') {
    // Keep set to "ALL" because it automatically includes all items.
    return prevSelectedLegendItems;
  }

  const newSelectedLegendItems = produce(prevSelectedLegendItems, (draftSelectedLegendItems) => {
    // Keep track of all the keys currently in the selectedLegendItems key-value store.
    const selectedLegendItemsSet = new Set<string>(Object.keys(draftSelectedLegendItems));
    // Iterate pie chart data and add any new items missing from the
    // prevSelectedLegendItems key-value store.
    for (let i = 0; i < pieChartData.length; i++) {
      const seriesId = pieChartData[i]?.[3];
      if (!seriesId) {
        continue;
      }

      // if series id is an array (i.e. we're looking at the aggregated slice which is made up of many series), iterate through the array
      if (Array.isArray(seriesId)) {
        seriesId.map((id) => {
          // Remove series names from the chart data as we see them, so that at the
          // end, we known which keys are no longer used and can be removed.
          selectedLegendItemsSet.delete(`${id}`);

          if (draftSelectedLegendItems[id] === undefined) {
            // If a series in the new data is not present in the previously selected legend items,
            // add it initialized as true, so it shows in the chart.
            draftSelectedLegendItems[id] = true;
          }
        });
      } else {
        // Remove series names from the chart data as we see them, so that at the
        // end, we known which keys are no longer used and can be removed.
        selectedLegendItemsSet.delete(`${seriesId}`);

        if (draftSelectedLegendItems[seriesId] === undefined) {
          // If a series in the new data is not present in the previously selected legend items,
          // add it initialized as true, so it shows in the chart.
          draftSelectedLegendItems[seriesId] = true;
        }
      }
    }

    // Keys remaining in the set do not exist in the new pie chart data. Remove them to avoid
    // the key-value store growing forever as data updates.
    if (selectedLegendItemsSet.size > 0) {
      for (const key of selectedLegendItemsSet) {
        delete draftSelectedLegendItems[key];
      }
    }
  });

  return newSelectedLegendItems;
}

function getSingleLegendItemSelectedState(
  pieChartData: PieChartDataset,
  focusedItemId: string
): SelectedLegendItemState {
  const newSelectedLegendItems: SelectedLegendItemRecordState = {};

  for (let i = 0; i < pieChartData.length; i++) {
    const seriesId = pieChartData[i]?.[3];
    if (!seriesId || Array.isArray(seriesId)) {
      continue;
    }

    newSelectedLegendItems[seriesId] = seriesId === focusedItemId;
  }

  return newSelectedLegendItems;
}

/**
 * Hook that manages the selected legend items state. Includes processing to update the legend
 * items as needed when data changes.
 */
export const useSelectedLegendItems = ({
  pieChartData,
  focusedItemId,
}: UseSelectedLegendItemsProps): UseSelectedLegendItemsResponse => {
  const [selectedLegendItems, setSelectedLegendItems] = useState<SelectedLegendItemState>('ALL');
  const isFocusedItemInitialized = useRef<boolean>(false);

  useEffect(() => {
    // Update legend items.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedLegendItems((prevSelectedLegendItems) => {
      return updateSelectedLegendItemsWithNewData(prevSelectedLegendItems, pieChartData);
    });
  }, [pieChartData]);

  useEffect(() => {
    // If focused item is specfied, adjust the legend to focus that item when we have
    // data. We only do this once to avoid overriding the user's legend interactions if the
    // data is refreshed.
    if (!isFocusedItemInitialized.current && focusedItemId && pieChartData.length) {
      isFocusedItemInitialized.current = true;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedLegendItems(getSingleLegendItemSelectedState(pieChartData, focusedItemId));
    } else if (isFocusedItemInitialized.current && !focusedItemId) {
      // If the focused item has been initialized and then was set to undefined, reset selected
      // items to 'ALL'.
      setSelectedLegendItems('ALL');
    }
  }, [pieChartData, focusedItemId]);

  return {
    selectedLegendItems,
    setSelectedLegendItems,
  };
};
